const _ = require("lodash");

/**
 * Functions that operate on activity objects, as they would be stored in
 * the user_activity table, and determine what other timers should also be
 * incremented when storing them. Almost all timer incrementing is done
 * via these returns.
 *
 * These functions associate to each activity an array of aux keys. These keys
 * take the form of arrays whose first element is a string naming type of
 * timer. Each aux key takes one of these forms:
 * - ["result", resultID]
 * - ["problem", resultID, problemID]
 * - ["library", chapterID, sectionID]
 * - ["theater", videoSetID, videoID]
 * - ["daily", space, resultID | null, library key | null, theater key | null]
 * - ["user", space]
 *
 * Note that some of these, especially the result aux, increment timers in
 * multiple tables, to match the fact that trainer:result:update originally
 * incremented a lot of different timeSpent values. You can see how each aux
 * is handled by looking at the implementation of user:updateActivityTimes.
 *
 * (The keys would normally be better as objects, since each array element has
 * a specific meaning, but it was important that these be trivial to serialize
 * for deduping purposes.)
 */

/**
 * Whether to include the problem aux.
 */
const INCLUDE_PROBLEM_AUX = false;

/**
 * Which sections/spaces count toward user total and daily activity times.
 */
const SECTIONS_FOR_TOTALS = [
	"trainer",
	"library",
	"theater",
	"lab",
	"beastbuilder",
	"leaderboards",
];

/**
 * A helper that just returns whether the result aux is applicable to this
 * activity. Useful for the frontend, which has to do some updates of result
 * timers without the backend's help.
 *
 * @param {object} activity
 * @returns {boolean}
 */
const useResultAux = function(activity) {
	if (!activity) return false;
	const sectionCheck =
		(activity.section === "trainer" ||
			activity.section === "lab" ||
			activity.section === "tutorial") &&
		activity.subsection === "problem";
	return (
		sectionCheck &&
		activity.objectID &&
		activity.type !== "summary" &&
		activity.type !== "completed" &&
		activity.type !== "intervened"
	);
};

/**
 * Returns the array of all aux keys for a single activity. The possible aux
 * keys are discussed at the top of this file.
 *
 * @param {object} activity
 * @returns {array[]}
 */
const getOneActivityAuxKeys = function(activity) {
	const auxKeys = [];
	if (!activity) {
		return auxKeys;
	}

	let dailyResultID = null;
	let dailyLibraryKey = null;
	let dailyTheaterKey = null;

	if (useResultAux(activity)) {
		dailyResultID = activity.objectID;
		auxKeys.push(["result", activity.objectID]);
		if (INCLUDE_PROBLEM_AUX && activity.subobjectID) {
			auxKeys.push(["problem", activity.objectID, activity.subobjectID]);
		}
	}

	const forLibrary =
		activity.section === "library" && activity.subsection === "chapter";
	if (forLibrary && activity.objectID && activity.subobjectID) {
		dailyLibraryKey = `${activity.subobjectID}`;
		auxKeys.push(["library", activity.objectID, activity.subobjectID]);
	}

	const forTheater =
		activity.section === "theater" && activity.subsection === "set";
	if (forTheater && activity.objectID && activity.subobjectID) {
		dailyTheaterKey = `${activity.objectID}-${activity.subobjectID}`;
		auxKeys.push(["theater", activity.objectID, activity.subobjectID]);
	}

	const forTotals = _.includes(SECTIONS_FOR_TOTALS, activity.section);
	if (forTotals) {
		auxKeys.push([
			"daily",
			activity.section,
			dailyResultID,
			dailyLibraryKey,
			dailyTheaterKey,
		]);
		auxKeys.push(["user", activity.section]);
	}

	return auxKeys;
};

/**
 * Given an array of activity objects, finds all the distinct aux keys and the
 * sum of the applicable activity amounts. The return is an array of {key,
 * amount} objects where key is an aux key, different for each array element,
 * and amount is a number.
 *
 * The newAmount field is used instead of amount for activity, since we can
 * handle incomplete activities here. See src's TimeTracking for more on how
 * this works.
 *
 * @param {object[]} activityList
 * @returns {object[]}
 */
const getActivityAux = function(activityList) {
	const auxTable = {};
	activityList.forEach(activity => {
		getOneActivityAuxKeys(activity).forEach(auxKey => {
			const entry = auxTable[auxKey.join(",")] || {
				key: auxKey,
				amount: 0,
			};
			entry.amount += activity.newAmount;
			auxTable[auxKey.join(",")] = entry;
		});
	});
	return _.values(auxTable);
};

module.exports = {
	useResultAux,
	getOneActivityAuxKeys,
	getActivityAux,
};
